import { format, parseISO, isSameDay, isBefore, isAfter, startOfMonth, endOfMonth, eachDayOfInterval, addMonths } from 'date-fns';
import { DailyEntry, MonthlyBreakdown, VacationDay } from '../types';

/**
 * Format a date string to display format
 */
export const formatDisplayDate = (dateString: string): string => {
  return format(parseISO(dateString), 'MMM dd, yyyy');
};

/**
 * Normalize a date to consistent midnight representation in local timezone
 * This ensures the date represents what the user selected, regardless of time components
 */
export const normalizeDate = (date: Date): Date => {
  // Create a new date with only the year, month, and day components in local timezone
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

/**
 * Format a date to ISO string (YYYY-MM-DD) preserving the selected date
 * regardless of timezone by explicitly setting only the date components
 */
export const formatISODate = (date: Date): string => {
  // First normalize the date to ensure we're working with midnight in local timezone
  const normalizedDate = normalizeDate(date);
  
  // Format to YYYY-MM-DD
  return format(normalizedDate, 'yyyy-MM-dd');
};

/**
 * Parse an ISO date string to a Date object with consistent timezone handling
 */
export const parseISODatePreserved = (dateString: string): Date => {
  // Parse the date components
  const [year, month, day] = dateString.split('-').map(Number);
  
  // Create a new Date object with local date components set to midnight
  return new Date(year, month - 1, day);
};

/**
 * Get today's date as ISO string
 */
export const getTodayISOString = (): string => {
  return formatISODate(new Date());
};

/**
 * Check if dates are the same day (ignoring time components)
 */
export const isSameDayNormalized = (date1: Date, date2: Date): boolean => {
  return date1.getFullYear() === date2.getFullYear() &&
         date1.getMonth() === date2.getMonth() &&
         date1.getDate() === date2.getDate();
};

/**
 * Check if a date is a vacation day - using normalized date comparison
 */
export const isVacationDay = (date: Date, vacationDays: VacationDay[]): boolean => {
  const normalizedDate = normalizeDate(date);
  return vacationDays.some(vd => {
    const vacationDate = parseISODatePreserved(vd.vacation_date);
    return isSameDayNormalized(normalizedDate, vacationDate);
  });
};

/**
 * Check if a day is within the user's defined work week
 */
export const isWorkDay = (date: Date, workWeekDays: number[]): boolean => {
  return workWeekDays.includes(date.getDay());
};

/**
 * Get an array of months between start and end dates, limited to today
 */
export const getMonthsInRange = (
  startDate: string, 
  endDate: string
): { month: string; displayName: string }[] => {
  const start = parseISO(startDate);
  const end = parseISO(endDate);
  const today = new Date();
  
  // Limit end date to today
  const limitedEnd = isBefore(today, end) ? today : end;
  
  const months: { month: string; displayName: string }[] = [];
  
  let currentMonth = startOfMonth(start);
  
  while (isBefore(currentMonth, limitedEnd) || isSameDay(currentMonth, limitedEnd)) {
    months.push({
      month: format(currentMonth, 'yyyy-MM'),
      displayName: format(currentMonth, 'MMMM yyyy')
    });
    currentMonth = addMonths(currentMonth, 1);
  }
  
  return months;
};

/**
 * Group daily entries by month
 */
export const groupEntriesByMonth = (
  entries: DailyEntry[],
  startDate: string,
  endDate: string
): MonthlyBreakdown[] => {
  // Get all months in the tracking period up to today
  const months = getMonthsInRange(startDate, endDate);
  
  // Create a monthly breakdown
  const monthlyBreakdowns = months.map(month => {
    // Get all entries for this month
    const monthEntries = entries.filter(entry => {
      const entryDate = parseISO(entry.entry_date);
      const monthStart = parseISO(`${month.month}-01`);
      const monthEnd = endOfMonth(monthStart);
      
      return (
        !isBefore(entryDate, monthStart) && 
        !isAfter(entryDate, monthEnd)
      );
    });
    
    // Calculate total hours for this month
    const totalHours = monthEntries.reduce(
      (sum, entry) => sum + entry.billed_hours,
      0
    );
    
    return {
      month: month.month,
      displayName: month.displayName,
      entries: monthEntries,
      totalHours
    };
  });
  
  return monthlyBreakdowns;
};

/**
 * Group daily entries by month and include all days in the month
 */
export const groupEntriesByMonthWithAllDays = (
  entries: DailyEntry[],
  startDate: string,
  endDate: string
): MonthlyBreakdown[] => {
  // Get all months in the tracking period up to today
  const months = getMonthsInRange(startDate, endDate);
  
  // Get today for comparison
  const today = new Date();
  // Set to end of day to include today in comparisons
  today.setHours(23, 59, 59, 999);
  
  // Create a monthly breakdown
  const monthlyBreakdowns = months.map(month => {
    // Get first and last day of month
    const monthStart = parseISO(`${month.month}-01`);
    const monthEnd = endOfMonth(monthStart);
    
    // Get all days in the month
    const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
    
    // Filter days to only include days that are:
    // 1. After or equal to the startDate
    // 2. Before or equal to today
    // NOTE: We removed the work day filter to show all days regardless of work week settings
    const startDateObj = parseISO(startDate);
    
    const relevantDays = daysInMonth.filter(day => {
      const isAfterOrEqualStart = !isBefore(day, startDateObj) || isSameDay(day, startDateObj);
      const isBeforeOrEqualToday = isBefore(day, today) || isSameDay(day, today);
      
      return isAfterOrEqualStart && isBeforeOrEqualToday;
    });
    
    // Format days and check for existing entries
    const formattedDays = relevantDays.map(day => {
      const formattedDate = formatISODate(day);
      
      // Check if an entry exists for this date
      const existingEntry = entries.find(entry => entry.entry_date === formattedDate);
      
      return {
        date: formattedDate,
        displayDate: format(day, 'EEE, MMM d'),
        dayOfWeek: day.getDay(),
        hasEntry: Boolean(existingEntry),
        entry: existingEntry || null
      };
    });
    
    // Get all entries for this month
    const monthEntries = entries.filter(entry => {
      const entryDate = parseISO(entry.entry_date);
      return !isBefore(entryDate, monthStart) && !isAfter(entryDate, monthEnd);
    });
    
    // Calculate total hours for this month
    const totalHours = monthEntries.reduce(
      (sum, entry) => sum + entry.billed_hours,
      0
    );
    
    return {
      month: month.month,
      displayName: month.displayName,
      entries: monthEntries,
      days: formattedDays,
      totalHours
    };
  });
  
  return monthlyBreakdowns;
};

/**
 * Check if a date is in the future
 */
export const isDateInFuture = (dateString: string): boolean => {
  const date = parseISO(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time to start of day for fair comparison
  return isAfter(date, today);
};

/**
 * Get all work days in a month
 */
export const getWorkDaysInMonth = (
  month: string,
  workWeekDays: number[],
  vacationDays: VacationDay[]
): Date[] => {
  const monthStart = parseISO(`${month}-01`);
  const monthEnd = endOfMonth(monthStart);
  
  // Get all days in month
  const allDays = eachDayOfInterval({ start: monthStart, end: monthEnd });
  
  // Filter to only work days that aren't vacation days
  return allDays.filter(date => {
    return (
      isWorkDay(date, workWeekDays) && 
      !isVacationDay(date, vacationDays)
    );
  });
};

/**
 * Round number to specified decimal places
 */
export const roundToDecimal = (num: number, decimalPlaces: number = 2): number => {
  const factor = Math.pow(10, decimalPlaces);
  return Math.round(num * factor) / factor;
};